import { apiClient } from './client';
import {
  transactionsResponseSchema,
  transactionSchema,
  batchTransactionsResponseSchema,
} from './schemas';
import { toDateString } from '@/lib/format';
import type {
  Transaction,
  TransactionsResponse,
  TransactionFilters,
  CreateTransactionData,
  UpdateTransactionData,
  BatchUpdateTransactionPatch,
} from './types';

function buildTransactionParams(filters?: TransactionFilters): string {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const params = new URLSearchParams();
  if (filters?.type) params.append('type', filters.type);
  params.append('from', filters?.from || toDateString(thirtyDaysAgo));
  params.append('to', filters?.to || toDateString(today));
  if (filters?.category) params.append('category', filters.category);
  if (filters?.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.append('offset', String(filters.offset));

  return params.toString();
}

export const transactionsApi = {
  async getAll(filters?: TransactionFilters): Promise<TransactionsResponse> {
    const queryString = buildTransactionParams(filters);
    return apiClient.get<TransactionsResponse>(`/transactions?${queryString}`, transactionsResponseSchema);
  },

  async create(data: CreateTransactionData): Promise<Transaction> {
    return apiClient.post<Transaction>('/transactions', data, transactionSchema);
  },

  async update(data: UpdateTransactionData): Promise<Transaction> {
    return apiClient.put<Transaction>('/transactions', data, transactionSchema);
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete(`/transactions/${id}`);
  },

  async batchCreate(transactions: CreateTransactionData[]): Promise<Transaction[]> {
    return apiClient.post<Transaction[]>(
      '/transactions/batch',
      { transactions },
      batchTransactionsResponseSchema,
    );
  },

  async batchUpdate(transactions: BatchUpdateTransactionPatch[]): Promise<Transaction[]> {
    return apiClient.put<Transaction[]>(
      '/transactions/batch',
      { transactions },
      batchTransactionsResponseSchema,
    );
  },
};
